import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BackendPagination } from "../models/pagination.model";
import {
	DataTableSortOrderType,
	ITablePagination,
} from "../models/components/table/table-pagination.model";
import { AlertReason } from "../models/entities/alert.model";
import { Task } from "../models/entities/task.model";
import { buildMyAlertsFilters } from "../pages/home/worklist/worklist.service";
import { hideResolvedAlert } from "./useHideResolvedAlert";
import { useTasks } from "./useTasks";
import { useTasksCount } from "./useTasksCount";

/**
 * The logged-in user's unresolved alerts of one reason, for the Home worklist
 * (KAN-2553 critical, KAN-2641 sustained out-of-range).
 *
 * There is no separate alerts endpoint: this reads the SAME `/api/tasks/` rows
 * as the Requires attention table and the participant-profile card, so a
 * resolve from any of the three surfaces is immediately reflected in the other
 * two (the query keys below are shared with `useUpdateTask`'s invalidation).
 *
 * Scope is `assignee == me`, always — see `buildMyAlertsFilters`.
 */

export const MY_ALERTS_QUERY_KEYS = ["participants-tasks", "participants-tasks-count"];

/**
 * Maria asked for the whole list rather than a top-N with a "see all", so this
 * requests one large page. 100 is far above any realistic number of alerts of
 * one reason assigned to one person; it also bounds the page, which matters because
 * this query now runs on every coach's login (plan §11.5).
 */
export const MY_ALERTS_PAGE_SIZE = 100;

export const MY_ALERTS_DEFAULT_PAGINATION: ITablePagination = {
	first: 0,
	rows: MY_ALERTS_PAGE_SIZE,
	page: 0,
	// Newest first from the server; the priority-then-newest ordering Maria
	// asked for is applied in the pure sort at the consumer.
	sortField: "taskDate",
	sortOrder: -1 as DataTableSortOrderType,
	search: undefined,
};

export const buildMyAlertsPagination = (
	assigneeId: number,
	reason: AlertReason,
	pagination?: Partial<ITablePagination>
): BackendPagination =>
	new BackendPagination({
		...MY_ALERTS_DEFAULT_PAGINATION,
		...pagination,
		filters: buildMyAlertsFilters(assigneeId, reason),
	});

export interface UseMyAlertsResult {
	alerts: Task[];
	count: number;
	isLoading: boolean;
	isError: boolean;
	error: unknown;
	/** Refetch this list AND the Requires attention surfaces that share the rows. */
	invalidate: () => void;
	/**
	 * Take one alert out of this section's cached list and count RIGHT NOW,
	 * before the server has answered. Resolves to a function that puts the
	 * cache back exactly as it was — call it if the resolve fails.
	 */
	hideAlert: (alertId: number) => Promise<() => void>;
}

export const useMyAlerts = (
	assigneeId: number | undefined | null,
	reason: AlertReason
): UseMyAlertsResult => {
	const queryClient = useQueryClient();

	const backendPagination = useMemo(
		() => buildMyAlertsPagination(assigneeId ?? -1, reason),
		[assigneeId, reason]
	);

	/**
	 * Refresh on every page entry, never on a timer (Maria's answer #9). Home is
	 * a route, so mounting IS entering the page; `refetchOnMount: "always"`
	 * makes that explicit instead of depending on the global default staleTime
	 * staying at 0. Window-focus refetching is off so leaving the tab open does
	 * not turn into background polling.
	 */
	const queryOptions = {
		enabled: !!assigneeId,
		staleTime: 0,
		refetchOnMount: "always" as const,
		refetchOnWindowFocus: false,
	};

	const tasksQuery = useTasks(backendPagination, queryOptions);
	const countQuery = useTasksCount(backendPagination, queryOptions);

	const isError = !!tasksQuery.error || !!countQuery.error;

	/**
	 * Optimistic resolve (KAN-2553 follow-up, Maria 2026-09-29: "when I resolve
	 * an alert, it doesn't immediately disappear").
	 *
	 * Without this the row only leaves once the PATCH has returned AND the
	 * 100-row `/api/tasks/` list has been refetched, which takes several seconds
	 * in prod. The removal itself is the shared `hideResolvedAlert` — the same
	 * one both Requires attention tables use — so this section's list and count
	 * (and any cached Requires attention page holding the row) drop it together.
	 */
	const hideAlert = (alertId: number) => hideResolvedAlert(queryClient, alertId);

	return {
		alerts: tasksQuery.data ?? [],
		// NEVER collapse an error into 0 — a failed load must not read as
		// "nothing needs your attention today" (plan §11.6). The section frame
		// hides the badge entirely when isError is true.
		count: countQuery.data ?? 0,
		isLoading: tasksQuery.isLoading || countQuery.isLoading,
		isError,
		error: tasksQuery.error ?? countQuery.error,
		invalidate: () =>
			MY_ALERTS_QUERY_KEYS.forEach((key) =>
				queryClient.invalidateQueries({ queryKey: [key] })
			),
		hideAlert,
	};
};
